// Steam boilers (1859 – c.1940): pressure, water glass and firing rate for every rig with a steam engine.
// rig.boiler = { firing, pressure (psi), safePressure (psi), water (0–1 of the gauge glass), valveLifting, lowNotified }
// Deterministic (no RNG). Diesel / gas / electric rigs never get a boiler; nothing here touches them.
//
//   safePressure = rated psi (by engine) × (0.7 + 0.3 × engine condition/100)   — a worn boiler is weaker
//   target ratio r* (pressure / safe) = FIRING[firing].target (+0.04 when working but not drilling — lighter load);
//     a banked fire (rig not working) settles at the 'bank' ratio. Pressure moves 60% of the way to target each day.
//   safety valve fitted: lifts at safe pressure → pressure capped at 1.02 × safe, wastes fuel & water.
//   engine power × (min(r*, valve ? 1 : ∞) / 0.80)^0.6  → exactly 1 at 'steady' (existing balance holds)
//   fuel/day × FIRING[firing].fuel (+0.15 while the valve lifts)
//   water: boils off 0.15/day × steam (drilling; ×0.6 other work) (+0.05 while lifting); a tender (tool dresser on
//     cable rigs — he fired the boiler — or a motorman on rotaries) feeds it back toward 0.75 at up to 0.30/day.
//   explosion exposure × pressureRisk(p/safe) × waterRisk(water) (both 1 at steady, full glass).
import { eqDef, stat, eraDollars } from './defs.js';
import { activeRigCrew } from './crew.js';
import { clamp, notify, spend, fmt$ } from './util.js';

export const FIRING_LEVELS = ['bank', 'steady', 'hard', 'crowd'];
export const FIRING = {
  bank:   { name: 'Banked',   target: 0.50, fuel: 0.60, steam: 0.50 },
  steady: { name: 'Steady',   target: 0.80, fuel: 1.00, steam: 1.00 },
  hard:   { name: 'Hard',     target: 0.93, fuel: 1.35, steam: 1.35 },
  crowd:  { name: 'Crowd it', target: 1.08, fuel: 1.80, steam: 1.80 },
};
// Rated working pressure: Drake's portable "Huntress" ~45 psi; stationary 20 hp ~80; 1900s twin rotary ~125.
const RATED_PSI = { steam_6hp: 45, steam_20hp: 80, steam_twin_rotary: 125 };
export const WATER_NORMAL = 0.75, WATER_LOW = 0.35, WATER_TOPUP = 0.8;
const BOIL_OFF = 0.15, LIFT_WASTE = 0.05, TENDER_FEED = 0.30, TENDERS = ['tool_dresser', 'motorman', 'fireman'];
const WORKING = new Set(['DRILLING', 'TRIPPING', 'RUNNING_CASING', 'FISHING', 'CLEANOUT', 'GAS_BLOW', 'GUSHER', 'COMPLETING', 'RIGGING_UP']);

const engineOf = (state, rig) => (rig.engineUid ? state.items[rig.engineUid] : null);
export function isSteamRig(state, rig) { const e = engineOf(state, rig); return !!e && /steam/.test(e.defId); }
const hasValve = (rig) => !!rig.safety?.upgrades?.includes('boiler_safety_valve');
const wellOf = (state, rig) => (rig.wellId ? state.wells[rig.wellId] : null);
function load(state, rig) { // 'drill' | 'work' | 'idle'
  const w = wellOf(state, rig);
  if (!w) return 'idle';
  if (w.status === 'DRILLING') return w.problem ? 'idle' : 'drill';
  return WORKING.has(w.status) || w.lift?.usesRig ? 'work' : 'idle';
}

export function ratedPsi(defId) { return RATED_PSI[defId] ?? stat(eqDef(defId), 'boilerPsi', 80); }
export function safePsi(state, rig) {
  const e = engineOf(state, rig);
  return Math.round(ratedPsi(e.defId) * (0.7 + 0.3 * clamp(e.condition ?? 100, 0, 100) / 100) * 10) / 10;
}
const firingOf = (rig) => (FIRING[rig.boiler?.firing] ? rig.boiler.firing : 'steady');
function targetRatio(firing, ld) {
  if (ld === 'idle') return FIRING.bank.target;
  return FIRING[firing].target + (ld === 'work' ? 0.04 : 0);
}
export const hasTender = (state, rig) => activeRigCrew(state, rig).some((c) => TENDERS.includes(c.role));
// A dedicated fireman (hired for the boiler, 1859–1945): feeds harder and to a fuller glass, keeps the gauge off the
// pin, fires evenly (fuel) and keeps the boiler clean (explosion risk). Only acts when one is on the rig, so games
// without one are unchanged.
const FIREMAN_FEED = 0.5, FIREMAN_MAX_RATIO = 0.98;
export const firemanOf = (state, rig) => activeRigCrew(state, rig).find((c) => c.role === 'fireman') ?? null;

// Lazily create / drop rig.boiler. Returns the boiler or null for non-steam rigs.
export function ensureBoiler(state, rig) {
  if (!isSteamRig(state, rig)) { if (rig.boiler) delete rig.boiler; return null; }
  if (!rig.boiler) {
    const safe = safePsi(state, rig);
    rig.boiler = { firing: 'steady', pressure: Math.round(safe * FIRING.bank.target), safePressure: safe, water: WATER_NORMAL, valveLifting: false, lowNotified: false };
  }
  return rig.boiler;
}

// ---- multipliers used by drilling / fuel / safety --------------------------------------
export function boilerPowerMult(state, rig) {
  if (!rig.boiler || !isSteamRig(state, rig)) return 1;
  let r = FIRING[firingOf(rig)].target;
  if (firemanOf(state, rig)) r = Math.min(r, FIREMAN_MAX_RATIO); // he won't let the needle reach the pin
  return Math.pow((hasValve(rig) ? Math.min(r, 1) : r) / FIRING.steady.target, 0.6);
}
export function boilerFuelMult(state, rig) {
  if (!rig.boiler || !isSteamRig(state, rig)) return 1;
  const f = firemanOf(state, rig);
  return (FIRING[firingOf(rig)].fuel + (rig.boiler.valveLifting ? 0.15 : 0)) * (f ? 0.97 - 0.06 * (f.skill ?? 50) / 100 : 1); // even firing saves coal
}
export function pressureRisk(ratio) { return Math.exp(5.5 * (ratio - FIRING.steady.target)) * (1 + 10 * Math.max(0, ratio - 1)); }
export function waterRisk(water) { return 1 + 40 * Math.max(0, WATER_LOW - water); }
export function boilerRiskMult(state, rig) {
  const b = rig.boiler;
  if (!b || !isSteamRig(state, rig)) return 1;
  return pressureRisk(b.pressure / Math.max(1, b.safePressure)) * waterRisk(b.water) * (firemanOf(state, rig) ? 0.65 : 1); // blowdowns, clean glass, an eye on the crown sheet
}
// Warning text that matches what the gauges show (safety.js boiler_explosion row).
export function boilerWarnText(state, rig) {
  const b = rig.boiler;
  if (!b) return null;
  if (b.valveLifting) return 'Safety valve screaming — she is blowing off steam';
  if (b.water < WATER_LOW) return 'Water low in the glass';
  if (b.pressure > b.safePressure) return 'Gauge needle hard against the pin';
  return null;
}

// ---- daily (called from tickSafety, before the incident rolls) ---------------------------
export function tickBoilers(state) {
  for (const rig of Object.values(state.rigs)) {
    const b = ensureBoiler(state, rig);
    if (!b) continue;
    const firing = firingOf(rig), ld = load(state, rig), valve = hasValve(rig);
    b.firing = firing;
    b.safePressure = safePsi(state, rig);
    const r = targetRatio(firing, ld);
    let p = b.pressure + (r * b.safePressure - b.pressure) * 0.6;
    const fm = firemanOf(state, rig);
    if (fm && p > b.safePressure * FIREMAN_MAX_RATIO) p = b.safePressure * FIREMAN_MAX_RATIO;
    b.valveLifting = valve && p >= b.safePressure;
    b.pressure = Math.round(p * 10) / 10;
    if (valve) b.pressure = Math.min(b.pressure, Math.floor(b.safePressure * 10.2) / 10);
    if (ld !== 'idle') {
      b.water -= BOIL_OFF * FIRING[firing].steam * (ld === 'drill' ? 1 : 0.6) + (b.valveLifting ? LIFT_WASTE : 0);
      if (fm) { if (b.water < WATER_TOPUP) b.water = Math.min(WATER_TOPUP, b.water + FIREMAN_FEED); }
      else if (hasTender(state, rig) && b.water < WATER_NORMAL) b.water = Math.min(WATER_NORMAL, b.water + TENDER_FEED);
    }
    b.water = Math.round(clamp(b.water, 0, 1) * 1000) / 1000;
    if (b.water < WATER_LOW && !b.lowNotified) {
      b.lowNotified = true;
      notify(state, 'warning', { rigUid: rig.uid, wellId: rig.wellId ?? null, incidentType: 'boiler_explosion', level: 'warn',
        text: `${rig.name}: water low in the glass (${Math.round(b.water * 100)}%). ${hasTender(state, rig) ? 'The fire is outrunning the feed pump — ease the firing' : 'Nobody is feeding the boiler — assign a tool dresser / motorman'} or top it up before the crown sheet burns.` });
    } else if (b.water >= WATER_LOW) b.lowNotified = false;
  }
}

// ---- actions --------------------------------------------------------------------------------
export function setFiringRateImpl(state, rigUid, level) {
  const rig = state.rigs[rigUid];
  if (!rig) return { ok: false, msg: 'No such rig.' };
  if (!FIRING[level]) return { ok: false, msg: `Firing rate must be one of ${FIRING_LEVELS.join(', ')}.` };
  const b = ensureBoiler(state, rig);
  if (!b) return { ok: false, msg: `${rig.name} has no steam boiler.` };
  b.firing = level;
  const pw = Math.round((boilerPowerMult(state, rig) - 1) * 100), fu = Math.round((FIRING[level].fuel - 1) * 100);
  const sgn = (x) => (x >= 0 ? `+${x}` : `${x}`);
  return { ok: true, msg: `${rig.name}: firing ${FIRING[level].name.toLowerCase()} — power ${sgn(pw)}%, fuel ${sgn(fu)}%${level === 'crowd' && !hasValve(rig) ? '. No safety valve: nothing stops the needle.' : '.'}` };
}

export function topUpBoilerImpl(state, rigUid) {
  const rig = state.rigs[rigUid];
  if (!rig) return { ok: false, msg: 'No such rig.' };
  const b = ensureBoiler(state, rig);
  if (!b) return { ok: false, msg: `${rig.name} has no steam boiler.` };
  if (b.water >= WATER_TOPUP - 1e-9) return { ok: false, msg: 'The glass is already full.' };
  const edef = eqDef(engineOf(state, rig).defId);
  const cost = Math.round(eraDollars(state, 0.1 * stat(edef, 'fuelPerDay', 0), edef.baseYear ?? edef.year ?? 1859) * 100) / 100;
  if (cost > 0 && !spend(state, cost, 'fuel')) return { ok: false, msg: `Needs ${fmt$(cost)} of fuel.` };
  b.water = WATER_TOPUP;
  b.pressure = Math.round(b.pressure * 0.85 * 10) / 10; // cold feedwater knocks the steam back
  b.lowNotified = false;
  return { ok: true, msg: `${rig.name}: boiler topped up (cold feed drops the gauge to ${Math.round(b.pressure)} psi).` };
}

// ---- views / migration ----------------------------------------------------------------------
export function boilerView(state, rig) {
  const b = rig.boiler;
  if (!b || !isSteamRig(state, rig)) return null;
  return { pressure: b.pressure, safePressure: b.safePressure, water: b.water, firing: firingOf(rig), valveLifting: !!b.valveLifting,
    valve: hasValve(rig), tender: hasTender(state, rig), fireman: firemanOf(state, rig)?.name ?? null, powerMult: boilerPowerMult(state, rig), fuelMult: boilerFuelMult(state, rig),
    riskMult: boilerRiskMult(state, rig) };
}
export function migrateBoilers(state) { for (const rig of Object.values(state.rigs ?? {})) ensureBoiler(state, rig); }
